import { useMemo, useState } from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatEnumValue } from '@/lib/formatters'
import { cn } from '@/lib/utils'

export type MetricComboboxOption = {
  id: string
  label: string
  labeledAs?: string | null
  sectionName?: string | null
  category?: string | null
  description?: string | null
  sortOrder?: number | null
}

type MetricComboboxGroupBy = 'section' | 'category' | 'none'

interface MetricComboboxProps {
  options: MetricComboboxOption[]
  value?: string
  onValueChange: (value: string) => void
  disabledOptionIds?: string[]
  disabledValue?: string
  disabled?: boolean
  isLoading?: boolean
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  className?: string
  contentClassName?: string
  triggerClassName?: string
  groupBy?: MetricComboboxGroupBy
  size?: 'default' | 'compact'
  variant?: 'default' | 'ghost'
  showFullValueTooltip?: boolean
}

function getGroupKey(
  option: MetricComboboxOption,
  groupBy: MetricComboboxGroupBy
) {
  if (groupBy === 'none') return 'Metrics'
  if (groupBy === 'category' && option.category)
    return formatEnumValue(option.category)
  return option.sectionName || 'Metrics'
}

function getSearchValue(option: MetricComboboxOption) {
  return [
    option.label,
    option.labeledAs,
    option.sectionName,
    option.category,
    option.description,
    option.id,
  ]
    .filter(Boolean)
    .join(' ')
}

function groupOptions(
  options: MetricComboboxOption[],
  groupBy: MetricComboboxGroupBy
): { key: string; heading?: string; items: MetricComboboxOption[] }[] {
  if (groupBy === 'none') {
    return [{ key: 'metrics', items: options }]
  }

  const buckets = new Map<string, MetricComboboxOption[]>()

  for (const option of options) {
    const key = getGroupKey(option, groupBy)
    const bucket = buckets.get(key) ?? []
    bucket.push(option)
    buckets.set(key, bucket)
  }

  const showHeadings = buckets.size > 1
  return Array.from(buckets.entries()).map(([key, items]) => ({
    key,
    heading: showHeadings ? key : undefined,
    items,
  }))
}

/**
 * Shared metric selector for Compass metric-label inputs.
 *
 * Metric lists can be long, so this component is intentionally searchable by
 * default. Use it instead of a plain Select whenever a user is choosing a metric.
 */
export function MetricCombobox({
  options,
  value,
  onValueChange,
  disabledOptionIds = [],
  disabledValue,
  disabled = false,
  isLoading = false,
  placeholder = 'Select metric',
  searchPlaceholder = 'Search metrics...',
  emptyText = 'No metrics found.',
  className,
  contentClassName,
  triggerClassName,
  groupBy = 'section',
  size = 'default',
  variant = 'default',
  showFullValueTooltip = false,
}: MetricComboboxProps) {
  const [open, setOpen] = useState(false)
  const selected = options.find((option) => option.id === value)
  const disabledIds = useMemo(() => {
    const ids = new Set(disabledOptionIds)
    if (disabledValue) ids.add(disabledValue)
    return ids
  }, [disabledOptionIds, disabledValue])
  const groups = useMemo(
    () => groupOptions(options, groupBy),
    [options, groupBy]
  )
  const fullValueLabel = selected ? selected.label : placeholder

  const triggerButton = (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      aria-label={fullValueLabel}
      disabled={disabled}
      className={cn(
        'w-full min-w-0 justify-between gap-2 border-primary/20 bg-transparent px-3 font-normal text-primary ring-offset-background hover:bg-primary/5 focus-visible:ring-1 focus-visible:ring-ring',
        size === 'compact' ? 'h-7 text-sm' : 'h-9 text-xs',
        variant === 'ghost' &&
          'w-auto max-w-56 border-primary/10 px-2 shadow-none hover:border-primary/20',
        triggerClassName,
        className
      )}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <Search className="size-3.5 shrink-0 text-primary/70" />
        <span
          className={cn(
            'min-w-0 truncate',
            selected ? 'text-primary' : 'text-primary/70'
          )}
        >
          {fullValueLabel}
        </span>
      </span>
      <ChevronDown className="size-3.5 shrink-0 text-primary/70" />
    </Button>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {showFullValueTooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6}>
            {fullValueLabel}
          </TooltipContent>
        </Tooltip>
      ) : (
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      )}
      <PopoverContent
        className={cn(
          'w-(--radix-popover-trigger-width) min-w-72 p-0',
          contentClassName
        )}
        align="start"
        data-radix-scroll-lock-ignore
        onWheel={(event) => event.stopPropagation()}
        onTouchMove={(event) => event.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList
            data-radix-scroll-lock-ignore
            onWheel={(event) => event.stopPropagation()}
            onTouchMove={(event) => event.stopPropagation()}
          >
            {isLoading ? (
              <CommandEmpty>Loading metrics...</CommandEmpty>
            ) : options.length === 0 ? (
              <CommandEmpty>{emptyText}</CommandEmpty>
            ) : (
              <>
                <CommandEmpty>{emptyText}</CommandEmpty>
                {groups.map((group) => (
                  <CommandGroup key={group.key} heading={group.heading}>
                    {group.items.map((option) => {
                      const isSelected = option.id === value
                      const isDisabled =
                        disabledIds.has(option.id) && !isSelected

                      return (
                        <CommandItem
                          key={option.id}
                          value={getSearchValue(option)}
                          disabled={isDisabled}
                          onSelect={() => {
                            if (isDisabled) return
                            onValueChange(option.id)
                            setOpen(false)
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-1 size-4',
                              isSelected ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-primary">
                              {option.label}
                            </div>
                            {option.description || option.sectionName ? (
                              <div className="truncate text-[11px] text-primary/70">
                                {option.description ?? option.sectionName}
                              </div>
                            ) : null}
                          </div>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                ))}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
